import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import { useTheme, ThemeToggle } from '../theme';
import * as api from '../api';
import { makeAuthStyles, errText } from './authStyles';

// External-link / "open URL in new tab" icon (monochrome, inherits button color).
function LinkIcon({ size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
         strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ display: 'block' }}>
      <path d="M15 3h6v6" />
      <path d="M10 14 21 3" />
      <path d="M19 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2h6" />
    </svg>
  );
}

// Pull (we fetch the pilot's position) vs Push (the device sends it to us). Arrow
// into / out of a baseline tray. Monochrome, inherits color.
function PullIcon({ size = 15 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
         strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ display: 'block' }}>
      <path d="M12 3v12" />
      <path d="M7 10l5 5 5-5" />
      <path d="M5 21h14" />
    </svg>
  );
}
function PushIcon({ size = 15 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
         strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ display: 'block' }}>
      <path d="M12 21V9" />
      <path d="M7 14l5-5 5 5" />
      <path d="M5 3h14" />
    </svg>
  );
}

export default function SettingsPage() {
  const { user, refreshUser, logout } = useAuth();
  const { colors } = useTheme();
  const S = makeAuthStyles(colors);
  const navigate = useNavigate();
  // Small icon-only button that opens an external site in a new tab, next to a key input.
  const extLinkStyle = {
    ...S.buttonAlt, display: 'flex', alignItems: 'center', justifyContent: 'center',
    textDecoration: 'none', padding: '0 12px', color: colors.text,
  };
  // Amber "⚠ Missing configuration" chip (used by the Pushover notification key).
  const warnChip = (
    <span style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, fontWeight: 700,
                   color: '#ff8c00', textTransform: 'none', letterSpacing: 0 }}>
      ⚠ Missing configuration
    </span>
  );
  const [form, setForm] = useState({
    puretrack_key: user?.puretrack_key || '',
    pushover_user_key: user?.pushover_user_key || '',
    xc_globe_uid: user?.xc_globe_uid || '',
  });
  const [error, setError] = useState('');
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState(false);
  // Single-open accordion for the location-input providers. All collapsed by default.
  const [openTab, setOpenTab] = useState(null);
  // OwnTracks config download state.
  const [otBusy, setOtBusy] = useState(false);
  const [otError, setOtError] = useState('');
  // Auto-configure (detect keys from current location) state. `detectingScope` is
  // the scope currently running ('all' | 'puretrack' | 'xcglobe') or null; `detectScope`
  // is the scope of the last completed run (drives where results render).
  const [detectingScope, setDetectingScope] = useState(null);
  const [detectScope, setDetectScope] = useState(null);
  const [detectResult, setDetectResult] = useState(null); // { puretrack, xcglobe, owntracks }
  const [detectError, setDetectError] = useState('');
  // PureTrack "multiple trackers nearby" candidate list, shown inside the tab for
  // the pilot to pick their own; null when there's nothing to disambiguate.
  const [ptCandidates, setPtCandidates] = useState(null);
  const [selecting, setSelecting] = useState(false);
  // Which source's enable/disable switch is mid-request (disables it briefly).
  const [togglingSource, setTogglingSource] = useState(null);
  // Pushover "TEST" button state; `pushResult` is { ok, text } or null.
  const [pushBusy, setPushBusy] = useState(false);
  const [pushResult, setPushResult] = useState(null);

  // While on Settings, re-pull the profile every 15s so a source's "ACTIVE" badge
  // flips on shortly after the backend latches its verified flag (no manual reload).
  useEffect(() => {
    const id = setInterval(() => { refreshUser(); }, 15000);
    return () => clearInterval(id);
  }, [refreshUser]);

  const set = (k) => (e) => {
    setForm(f => ({ ...f, [k]: e.target.value }));
    setSaved(false);
    // Editing the key invalidates the last TEST verdict.
    if (k === 'pushover_user_key') setPushResult(null);
  };

  // No location source is usable when nothing is set and nothing has verified.
  const noLocationSource = !(
    form.puretrack_key || form.xc_globe_uid ||
    user?.puretrack_verified || user?.xcglobe_verified || user?.owntracks_verified
  );

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError(''); setSaved(false);
    try {
      await api.updateMe({
        puretrack_key: form.puretrack_key,
        pushover_user_key: form.pushover_user_key,
        xc_globe_uid: form.xc_globe_uid,
      });
      await refreshUser();
      setSaved(true);
    } catch (err) {
      setError(errText(err));
    } finally {
      setBusy(false);
    }
  };

  // Send a real Pushover notification to the key currently in the field (saved or
  // not), so the pilot can confirm alerts actually reach their phone.
  const sendTestPush = async () => {
    setPushBusy(true); setPushResult(null);
    try {
      await api.testPush(form.pushover_user_key);
      setPushResult({ ok: true, text: 'Sent ✓ — check your phone' });
    } catch (err) {
      setPushResult({ ok: false, text: errText(err) });
    } finally {
      setPushBusy(false);
    }
  };

  // Import the pilot's OwnTracks config straight into the app via the
  // owntracks:///config?inline=<base64> URL scheme (one tap, no file handling).
  const configureOwntracks = async () => {
    setOtBusy(true); setOtError('');
    try {
      const cfg = await api.getOwntracksConfig();
      const inline = btoa(unescape(encodeURIComponent(JSON.stringify(cfg))));
      const a = document.createElement('a');
      a.href = `owntracks:///config?inline=${inline}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (err) {
      setOtError(errText(err));
    } finally {
      setOtBusy(false);
    }
  };

  // Get the phone's location, with error messages that name the actual cause.
  const currentPosition = () => new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      return reject(new Error('Location is not available on this device.'));
    }
    // Chrome (esp. Android) blocks geolocation outside a secure context, so an
    // http://<ip>:port dev URL fails even with the OS/site permission granted —
    // the #1 reason "location is on but it still won't work".
    if (window.isSecureContext === false) {
      return reject(new Error('Location needs a secure (HTTPS) connection — open the app over https and try again.'));
    }
    const ok = (p) => resolve({ lat: p.coords.latitude, lng: p.coords.longitude });
    const fail = (err) => {
      if (err && err.code === 1) {
        reject(new Error('Location permission denied for this site. Tap the lock/⋮ icon in the address bar → Permissions → allow Location, then retry.'));
      } else if (err && err.code === 3) {
        reject(new Error('Timed out getting a GPS fix. Make sure GPS is on, step outside, and try again.'));
      } else {
        reject(new Error('Could not get your location. Turn on GPS/location and try again.'));
      }
    };
    // Try a quick (possibly cached) fix first; if that times out, retry once with
    // high accuracy and a longer budget — an Android GPS cold-start can take >10s.
    navigator.geolocation.getCurrentPosition(
      ok,
      (err) => {
        if (err && err.code === 3) {
          navigator.geolocation.getCurrentPosition(ok, fail,
            { enableHighAccuracy: true, maximumAge: 0, timeout: 25000 });
        } else {
          fail(err);
        }
      },
      { enableHighAccuracy: false, maximumAge: 60000, timeout: 8000 },
    );
  });

  // Detect keys from the pilot's current location. `source` scopes it to one
  // provider (undefined = all). The backend auto-saves an unambiguous match; we
  // refresh the profile so inputs + status dots reflect what was stored, and merge
  // the returned provider(s) into detectResult so a single run only updates its line.
  const autoDetect = async (source) => {
    const scope = source || 'all';
    setDetectingScope(scope); setDetectError(''); setPtCandidates(null);
    if (source) setDetectResult(r => ({ ...(r || {}), [source]: undefined }));
    else setDetectResult(null);
    try {
      const { lat, lng } = await currentPosition();
      const res = await api.detectLocationKeys(lat, lng, source);
      setDetectResult(r => ({ ...(source ? (r || {}) : {}), ...res }));
      setDetectScope(scope);
      // Several PureTrack trackers near the pilot → let them pick their own in the tab.
      if (res.puretrack?.status === 'multiple' && res.puretrack.candidates?.length) {
        setPtCandidates(res.puretrack.candidates);
        setOpenTab('puretrack');
      }
      const me = await refreshUser();
      if (me) setForm(f => ({ ...f, puretrack_key: me.puretrack_key || '', xc_globe_uid: me.xc_globe_uid || '' }));
    } catch (err) {
      setDetectError(errText(err));
    } finally {
      setDetectingScope(null);
    }
  };

  // Pilot picked their tracker from the "multiple nearby" list → save it + activate.
  const selectPuretrack = async (key) => {
    setSelecting(true); setDetectError('');
    try {
      await api.selectLocationKey('puretrack', key);
      setPtCandidates(null);
      const me = await refreshUser();
      if (me) setForm(f => ({ ...f, puretrack_key: me.puretrack_key || '' }));
      setDetectResult(r => (r ? { ...r, puretrack: { status: 'found', id: key } } : r));
    } catch (err) {
      setDetectError(errText(err));
    } finally {
      setSelecting(false);
    }
  };

  // Enable/disable a location source. Disabled sources are ignored by the guard
  // (backend enforces it); we persist via the profile PATCH and refresh.
  const toggleSource = async (source, enabled) => {
    setTogglingSource(source); setError('');
    try {
      await api.updateMe({ [`${source}_enabled`]: enabled });
      await refreshUser();
    } catch (err) {
      setError(errText(err));
    } finally {
      setTogglingSource(null);
    }
  };

  // One-line, color-coded result for a single provider's detection outcome.
  const providerResult = (label, r) => {
    if (!r) return null;
    let color = colors.textMuted;
    let text;
    switch (r.status) {
      case 'found':
        color = '#66cc66';
        text = `${label}: active${r.distance_m != null ? ` (≈ ${r.distance_m} m)` : ''} ✓`;
        break;
      case 'multiple':
        color = '#ff8c00';
        text = r.candidates
          ? `${label}: several trackers nearby — pick yours below`
          : `${label}: ${r.count} trackers nearby — do this at home, away from others`;
        break;
      case 'none':
        text = `${label}: not active`;
        break;
      case 'unavailable':
        text = `${label}: detection unavailable`;
        break;
      default:
        color = '#ff6666';
        text = `${label}: lookup failed`;
    }
    return <div style={{ ...S.hint, color, marginTop: 4 }}>{text}</div>;
  };

  // Compact "auto configure from my current location" button, sits next to the
  // key input (where the external-link button used to be). Labelled rather than
  // iconic — a crosshair glyph doesn't read as "detect my keys" — so the text is
  // stacked on two lines to keep the button as narrow as the input row allows.
  const autoConfigureButton = (source) => (
    <button
      type="button" onClick={() => autoDetect(source)} disabled={!!detectingScope}
      title="Auto configure from your current location" aria-label="Auto configure from your location"
      style={{ ...extLinkStyle, flexDirection: 'column', gap: 1, padding: '0 8px',
               color: colors.text, fontSize: 9, fontWeight: 700, letterSpacing: 1, lineHeight: 1.15 }}
    >
      {detectingScope === source ? '…' : <><span>AUTO</span><span>CONFIGURE</span></>}
    </button>
  );
  // Result/error line for a per-provider auto-configure run, shown under the input.
  const autoConfigureResult = (source, label) => (
    detectScope === source ? (
      <>
        {detectError && <div style={{ ...S.hint, color: '#ff6666', marginTop: 6 }}>{detectError}</div>}
        {!detectError && detectResult && providerResult(label, detectResult[source])}
      </>
    ) : null
  );

  const doLogout = () => { logout(); navigate('/login', { replace: true }); };

  // Accordion row inside a shared bordered container. Clicking the header toggles
  // this row (single-open); the active row gets a red accent bar + tinted header,
  // a rotating chevron, and a height-animated body.
  const Tab = ({ id, title, subtitle, enabled, verified, configured, mode, first, body }) => {
    const open = openTab === id;
    // Off → muted dot, no ACTIVE badge. On → green once a fix has arrived, amber if a
    // key is set but unconfirmed, grey otherwise.
    const dotColor = !enabled ? colors.textMuted : (verified ? '#66cc66' : (configured ? '#ff8c00' : colors.textMuted));
    const toggle = () => setOpenTab(open ? null : id);
    return (
      <div style={{ borderTop: first ? 'none' : `1px solid ${colors.border}` }}>
        <div
          role="button" tabIndex={0}
          onClick={toggle}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggle(); } }}
          style={{
            width: '100%', boxSizing: 'border-box', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            gap: 8, padding: '13px 14px', paddingLeft: 14, cursor: 'pointer', font: 'inherit',
            color: open ? colors.textStrong : colors.text,
            background: open ? colors.surfaceActive : 'transparent',
            borderLeft: `3px solid ${open ? colors.north : 'transparent'}`,
            fontSize: 15, fontWeight: open ? 800 : 600, letterSpacing: 0.2,
            transition: 'background 140ms ease, border-color 140ms ease, color 140ms ease',
          }}
        >
          <span style={{ display: 'flex', flexDirection: 'column', gap: 3, minWidth: 0, flex: 1, opacity: enabled ? 1 : 0.55 }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ width: 8, height: 8, borderRadius: '50%', background: dotColor, flex: '0 0 auto' }} />
              {title}
              <span
                title={mode === 'push' ? 'Your device sends its position to us' : 'We fetch your position'}
                style={{ display: 'flex', alignItems: 'center', color: colors.textMuted, flex: '0 0 auto' }}
              >
                {mode === 'push' ? <PushIcon /> : <PullIcon />}
              </span>
              {enabled && verified && (
                <span style={{ fontSize: 10, fontWeight: 800, letterSpacing: 0.6, color: '#fff',
                               background: '#3a9d3a', borderRadius: 4, padding: '2px 5px', flex: '0 0 auto' }}>
                  ACTIVE
                </span>
              )}
            </span>
            {subtitle && (
              <span style={{ fontSize: 11, fontWeight: 400, color: colors.textMuted, lineHeight: 1.3, textAlign: 'left' }}>
                {subtitle}
              </span>
            )}
          </span>
          {/* Obvious expand/collapse affordance: a circular button-like chevron. */}
          <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center',
                         width: 28, height: 28, borderRadius: '50%', flex: '0 0 auto',
                         border: `1px solid ${colors.border}`, background: open ? colors.surface : colors.bg,
                         transform: open ? 'rotate(90deg)' : 'rotate(0deg)',
                         transition: 'transform 180ms ease, background 140ms ease' }}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke={colors.text} strokeWidth="2.5"
                 strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ display: 'block' }}>
              <path d="M9 6l6 6-6 6" />
            </svg>
          </span>
        </div>
        <div style={{ display: 'grid', gridTemplateRows: open ? '1fr' : '0fr',
                      transition: 'grid-template-rows 260ms cubic-bezier(0.4, 0, 0.2, 1)',
                      background: open ? colors.surfaceActive : 'transparent' }}>
          <div style={{ overflow: 'hidden', minHeight: 0 }}>
            <div style={{ padding: '2px 14px 14px', borderLeft: `3px solid ${open ? colors.north : 'transparent'}`,
                          opacity: open ? 1 : 0,
                          transform: open ? 'translateY(0)' : 'translateY(-6px)',
                          transition: open
                            ? 'opacity 220ms ease 60ms, transform 260ms cubic-bezier(0.4, 0, 0.2, 1) 60ms'
                            : 'opacity 140ms ease, transform 180ms ease' }}>
              {/* Opt-out: check to disable this source (default is enabled). */}
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14,
                              cursor: togglingSource === id ? 'default' : 'pointer',
                              fontSize: 13, fontWeight: 700, color: enabled ? colors.textMuted : '#ff8c00' }}>
                <input
                  type="checkbox" checked={!enabled} disabled={togglingSource === id}
                  onChange={(e) => toggleSource(id, !e.target.checked)}
                  style={{ width: 16, height: 16, accentColor: '#ff8c00', cursor: 'inherit' }}
                />
                Disabled
              </label>
              {body}
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div style={S.page}>
      <form style={{ ...S.card, position: 'relative' }} onSubmit={submit}>
        <button
          type="button" onClick={() => navigate('/')} title="Back to radar" aria-label="Back to radar"
          style={{ ...S.buttonAlt, position: 'absolute', top: 14, left: 14, width: 'auto', margin: 0,
                   padding: '6px 11px', fontSize: 18, lineHeight: 1 }}
        >
          ←
        </button>
        <h1 style={S.title}>Settings</h1>
        <div style={S.field}>
          <label style={S.label}>Username</label>
          <input style={{ ...S.input, opacity: 0.6 }} value={user?.username || ''} readOnly disabled />
        </div>

        {/* Location input configuration — one provider open at a time. */}
        <div style={S.field}>
          <label style={S.label}>Location input configuration</label>

          {/* Auto-detect: find the pilot's PureTrack & XCGlobe keys from their
              current location. Meant to be run at home (only their tracker nearby). */}
          <div style={{ marginTop: 6, marginBottom: 10 }}>
            <div style={{ ...S.hint, marginTop: 6 }}>
              Activate your instrumentation/apps like you are flying, make sure you are ACTUALLY displayed on the target platform, then click the button!
            </div>
            {detectError && detectScope === 'all' && <div style={{ ...S.hint, color: '#ff6666', marginTop: 4 }}>{detectError}</div>}
            {detectScope === 'all' && detectResult && (
              <div style={{ marginTop: 6 }}>
                {providerResult('PureTrack', detectResult.puretrack)}
                {providerResult('XCGlobe', detectResult.xcglobe)}
                {providerResult('OwnTracks', detectResult.owntracks)}
              </div>
            )}
            <button style={{ ...S.buttonAlt, margin: 0 }} type="button" onClick={() => autoDetect()} disabled={!!detectingScope}>
              {detectingScope === 'all' ? 'Detecting…' : '⌖ Auto detect & configure ALL'}
            </button>

          </div>

          {/* Single warning above the tabs when the pilot has no usable source at all. */}
          {noLocationSource && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 6,
                          fontSize: 12, fontWeight: 700, color: '#ff8c00' }}>
              ⚠ Missing configuration — set up at least one location source below.
            </div>
          )}

          <div style={{ display: 'flex', flexDirection: 'column', marginTop: 6,
                        border: `1px solid ${colors.border}`, borderRadius: 10, overflow: 'hidden',
                        background: colors.surface }}>
            <Tab
              first
              id="owntracks" title="OwnTracks" mode="push"
              subtitle="Best — fastest, most accurate, your phone pushes the location directly to the radar. Requires the OwnTracks app."
              enabled={user?.owntracks_enabled !== false}
              verified={!!user?.owntracks_verified} configured={false}
              body={
                <>
                  <div style={{ ...S.hint, marginBottom: 10 }}>
                    Install the OwnTracks app, go to info -> settings -> remote control (bottom) -> <b>set Allow external configuration</b>, only when, use the button to configure it for you
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button style={{ ...S.buttonAlt, margin: 0, flex: 1, minWidth: 0 }} type="button" onClick={configureOwntracks} disabled={otBusy}>
                      {otBusy ? 'Preparing…' : 'Insert configuration to OwnTracks app'}
                    </button>
                    {autoConfigureButton('owntracks')}
                  </div>
                  {autoConfigureResult('owntracks', 'OwnTracks')}
                  {otError && <div style={{ ...S.hint, color: '#ff6666', marginTop: 8 }}>{otError}</div>}
                </>
              }
            />
            <Tab
              id="puretrack" title="PureTrack" mode="pull"
              subtitle="OKish!"
              enabled={user?.puretrack_enabled !== false}
              verified={!!user?.puretrack_verified} configured={!!form.puretrack_key}
              body={
                <>
                  {ptCandidates && ptCandidates.length > 0 && (
                    <div style={{ marginBottom: 12 }}>
                      <div style={{ ...S.hint, marginBottom: 6, color: '#ff8c00', fontWeight: 700 }}>
                        Several trackers near you — pick yours:
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                        {ptCandidates.map((c) => (
                          <button
                            key={c.key} type="button" onClick={() => selectPuretrack(c.key)} disabled={selecting}
                            style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8,
                                     textAlign: 'left', cursor: 'pointer', padding: '9px 11px',
                                     borderRadius: 8, border: `1px solid ${colors.border}`,
                                     background: colors.surface, color: colors.text, font: 'inherit' }}
                          >
                            <span style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 }}>
                              <span style={{ fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {c.label || '(no label)'}
                              </span>
                              <span style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4,
                                               fontSize: 12, fontWeight: 700, color: colors.textStrong,
                                               background: colors.surfaceActive, border: `1px solid ${colors.border}`,
                                               borderRadius: 5, padding: '2px 7px', maxWidth: '100%',
                                               overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                  <PullIcon size={12} />
                                  {c.receiver_name || 'unknown receiver'}
                                </span>
                                {c.distance_m != null && (
                                  <span style={{ fontSize: 11, color: colors.textMuted }}>≈ {c.distance_m} m</span>
                                )}
                                {c.last_seen_ago && (
                                  <span style={{ fontSize: 11, color: colors.textMuted }}>· {c.last_seen_ago}</span>
                                )}
                              </span>
                            </span>
                            <span style={{ fontSize: 12, fontWeight: 700, color: colors.textMuted, flex: '0 0 auto' }}>Select</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                  <div style={S.labelRow}>
                    <label style={S.label}>PureTrack key</label>
                    <Link to="/instructions/puretrack" style={S.linkInline}>Instructions</Link>
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <input style={{ ...S.input, flex: 1, minWidth: 0 }} value={form.puretrack_key} onChange={set('puretrack_key')} autoCapitalize="none" autoCorrect="off" />
                    {autoConfigureButton('puretrack')}
                  </div>
                  {autoConfigureResult('puretrack', 'PureTrack')}
                </>
              }
            />
            <Tab
              id="xcglobe" title="XCGlobe" mode="pull"
              subtitle="Sadly, no heading (azimuth) exposed from XCGlobe — calulations will be less precise."
              enabled={user?.xcglobe_enabled !== false}
              verified={!!user?.xcglobe_verified} configured={!!form.xc_globe_uid}
              body={
                <>
                  <div style={S.labelRow}>
                    <label style={S.label}>XCGlobe user ID</label>
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <input style={{ ...S.input, flex: 1, minWidth: 0 }} value={form.xc_globe_uid} onChange={set('xc_globe_uid')} autoCapitalize="none" autoCorrect="off" />
                    {autoConfigureButton('xcglobe')}
                  </div>
                  {autoConfigureResult('xcglobe', 'XCGlobe')}
                </>
              }
            />
          </div>
        </div>

        <div style={S.field}>
          <div style={S.labelRow}>
            <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <label style={S.label}>Pushover user key</label>
              {!form.pushover_user_key && warnChip}
            </span>
            <Link to="/instructions/pushover" style={S.linkInline}>Instructions</Link>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <input style={{ ...S.input, flex: 1, minWidth: 0 }} value={form.pushover_user_key} onChange={set('pushover_user_key')} autoCapitalize="none" autoCorrect="off" />
            <button
              type="button" onClick={sendTestPush} disabled={pushBusy || !form.pushover_user_key}
              title="Send a test push notification to this key"
              style={{ ...extLinkStyle, fontSize: 12, fontWeight: 700, letterSpacing: 1,
                       opacity: form.pushover_user_key ? 1 : 0.5 }}
            >
              {pushBusy ? '…' : 'TEST'}
            </button>
            <a href="https://pushover.net" target="_blank" rel="noopener noreferrer" style={extLinkStyle} title="Open pushover.net" aria-label="Open pushover.net"><LinkIcon /></a>
          </div>
          {pushResult && (
            <div style={{ ...S.hint, color: pushResult.ok ? '#66cc66' : '#ff6666', marginTop: 6 }}>
              {pushResult.text}
            </div>
          )}
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
          <label style={S.label}>Default appearance</label>
          <ThemeToggle />
        </div>
        <div style={S.error}>{error || (saved ? <span style={{ color: '#66cc66' }}>Saved ✓</span> : '')}</div>
        <button style={S.button} type="submit" disabled={busy}>{busy ? 'Saving…' : 'Save'}</button>

        <button style={S.link} type="button" onClick={doLogout}>Log out</button>
      </form>
    </div>
  );
}
